import React from 'react';
import {
  Badge,
  Button,
  CaretDownIcon,
  CaretUpIcon,
  DoubleCaretVerticalIcon,
  DuplicateIcon,
  EditIcon,
  Icon,
  IconButton,
  MatTooltip,
  Menu,
  MinusIcon,
  MoreIcon,
  Pane,
  Popover,
  Position,
  TableCell,
  TableHeaderCell,
  TableRow,
  TickCircleIcon,
  TrashIcon,
  Text,
  Tooltip,
  Checkbox,
  BanCircleIcon,
} from 'evergreen-ui';

// Material-UI imports
import {
  IconButton as MuiIconButton,
  Menu as MuiMenu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip as MuiTooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIconMui from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';

import { convertDate, convertDate2, convertDate3 } from '../../helpers/dateConverter';
import { getNameAndColorEntryType } from '../../helpers/entriesHelper';
import { formatToMoneyWithSymbol } from '../../helpers/moneyFormatter';
import { isEntryReconciled, isTransferReconciled } from '../../helpers/reconciliationHelpers';
import { createColumnHelper } from '@tanstack/react-table';

const columnHelper = createColumnHelper();

// Component for Material-UI Actions Menu
const EntriesActionMenu = ({
  entry,
  currentRole,
  openDialog,
  openCopyDialog,
  openTransferDialog,
  openInitialBalanceDialog,
  openTransferCopyDialog,
  openDeleteDialog,
  statusPaymentMutation,
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const isReconciled =
    entry.journalEntryType === 5 ? isTransferReconciled(entry) : isEntryReconciled(entry);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAction = (action) => {
    action();
    handleClose();
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <MuiIconButton
        aria-label="mais ações"
        aria-controls={open ? 'actions-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        disabled={currentRole.data.userRole === 'ANALISTA'}
        size="small"
        sx={{
          color: '#6c757d',
          '&:hover': {
            backgroundColor: '#f8f9fa',
            color: '#495057',
          },
          '&:disabled': {
            color: '#adb5bd',
          },
          transition: 'all 0.2s ease-in-out',
        }}
      >
        <MoreVertIcon />
      </MuiIconButton>
      <MuiMenu
        id="actions-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'actions-button',
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            '& .MuiMenuItem-root': {
              transition: 'all 0.2s ease-in-out',
              borderRadius: 1,
              margin: '2px 4px',
            },
          },
        }}
      >
        {entry.journalEntryType !== 2 && (
          <MenuItem
            onClick={() =>
              handleAction(() => {
                entry.journalEntryType === 5
                  ? openTransferCopyDialog(entry)
                  : openCopyDialog(entry);
              })
            }
            sx={{
              color: '#007bff',
              '&:hover': {
                backgroundColor: '#e3f2fd',
              },
              '& .MuiListItemIcon-root': {
                color: '#007bff',
              },
            }}
          >
            <ListItemIcon>
              <ContentCopyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Criar cópia</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() =>
            handleAction(() => {
              entry.journalEntryType === 5
                ? openTransferDialog(entry)
                : entry.journalEntryType === 2
                ? openInitialBalanceDialog(entry)
                : openDialog(entry);
            })
          }
          sx={{
            color: '#fd7e14',
            '&:hover': {
              backgroundColor: '#fff3cd',
            },
            '& .MuiListItemIcon-root': {
              color: '#fd7e14',
            },
          }}
        >
          <ListItemIcon>
            <EditIconMui fontSize="small" />
          </ListItemIcon>
          <ListItemText>Editar</ListItemText>
        </MenuItem>
        <MuiTooltip
          title={isReconciled ? 'Não é possível excluir um lançamento conciliado.' : ''}
          disableHoverListener={!isReconciled}
        >
          <span>
            <MenuItem
              disabled={isReconciled}
              onClick={() => handleAction(() => openDeleteDialog(entry))}
              sx={{
                color: '#dc3545',
                '&:hover': {
                  backgroundColor: '#f8d7da',
                },
                '& .MuiListItemIcon-root': {
                  color: '#dc3545',
                },
              }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Deletar</ListItemText>
            </MenuItem>
          </span>
        </MuiTooltip>
        {entry.journalEntryType === 1 && (
          <MenuItem
            onClick={() =>
              handleAction(() => {
                const paid = {
                  ...entry,
                  statusPayment: true,
                  statusString: 'Pago',
                };
                const pending = {
                  ...entry,
                  statusPayment: false,
                  statusString: 'Vencido',
                };

                entry.statusPayment ? statusPaymentMutation(pending) : statusPaymentMutation(paid);
              })
            }
            sx={{
              color: entry.statusPayment ? '#fd7e14' : '#28a745',
              '&:hover': {
                backgroundColor: entry.statusPayment ? '#fff3cd' : '#d4edda',
              },
              '& .MuiListItemIcon-root': {
                color: entry.statusPayment ? '#fd7e14' : '#28a745',
              },
            }}
          >
            <ListItemIcon>
              {entry.statusPayment ? (
                <PendingIcon fontSize="small" />
              ) : (
                <CheckCircleIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText>
              {entry.statusPayment ? 'Marcar como Vencido' : 'Marcar como Pago'}
            </ListItemText>
          </MenuItem>
        )}
      </MuiMenu>
    </div>
  );
};

export const createEntriesTableColumns = (
  currentRole,
  openDialog,
  openCopyDialog,
  openTransferDialog,
  openInitialBalanceDialog,
  openTransferCopyDialog,
  openDeleteDialog,
  statusPaymentMutation,
  table, // Table instance
  userNamesById // Map<vitaUserId, displayName>, usado nas colunas Criador/Atualizado por
) => {
  const getUserDisplayName = (id) => (id && userNamesById?.get(id)) || id;

  return [
    columnHelper.accessor('entryType', {
      enableHiding: false,
      header: 'Tipo',
      id: 'entryType',
      enableSorting: false,
      size: 120,
      cell: (info) => {
        let val = getNameAndColorEntryType(info.cell.row.original);
        return <Badge color={`${val[1]}`}>{val[0]}</Badge>;
      },
    }),
    columnHelper.accessor('dateCreate', {
      header: 'Criado em',
      id: 'criadoEm',
      size: 120,
      cell: (info) => {
        return convertDate3(info.cell.getValue());
      },
    }),
    columnHelper.accessor('dateUpdate', {
      header: 'Atualizado em',
      id: 'atualizadoEm',
      size: 120,
      cell: (info) => {
        return convertDate3(info.cell.getValue());
      },
    }),
    columnHelper.accessor('dateCompetencia', {
      header: 'Competência',
      id: 'competencia',
      cell: (info) => {
        return convertDate2(info.cell.getValue());
      },
      sortingFn: 'datetime',
    }),
    columnHelper.accessor('dateVencimento', {
      header: 'Vencimento',
      id: 'vencimento',
      cell: (info) => {
        return convertDate2(info.cell.getValue());
      },
      sortingFn: 'datetime',
    }),
    {
      header: 'Conta',
      id: 'conta',
      accessorFn: (row) => row.bankAccountName,
      enableSorting: false,
      filterFn: 'equalsString',
      cell: (info) => {
        const value = info.getValue();
        // Show fallback text for empty values
        return value && value.trim() !== '' ? value : '-';
      },
    },
    {
      header: 'Classificação DRE',
      id: 'DRE',
      accessorFn: (row) => row.dreClassification,
      enableSorting: false,
      filterFn: 'includesString',
      cell: (info) => {
        const value = info.getValue();
        return value && value.trim() !== '' ? value : '-';
      },
    },
    {
      header: 'Agrupamento',
      id: 'Agrupamento',
      accessorFn: (row) => row.dreGroupingName,
      enableSorting: false,
      filterFn: 'includesString',
      cell: (info) => {
        const value = info.getValue();
        return value && value.trim() !== '' ? value : '-';
      },
    },
    {
      header: 'Plano de Contas',
      id: 'Plano',
      accessorFn: (row) => row.accountPlanName,
      enableSorting: false,
      filterFn: 'includesString',
      cell: (info) => {
        const value = info.getValue();
        return value && value.trim() !== '' ? value : '-';
      },
    },
    {
      header: 'Categoria',
      id: 'categoria',
      accessorFn: (row) => row.categoryName,
      enableSorting: false,
      filterFn: 'includesString',
      cell: (info) => {
        const value = info.getValue();
        // Show fallback text for empty values
        return value && value.trim() !== '' ? value : '-';
      },
    },
    {
      header: 'Descrição',
      id: 'descricao',
      accessorFn: (row) => row.description,
      cell: (info) => {
        const value = info.cell.getValue();
        const truncatedValue = value && value.length > 40 ? `${value.substring(0, 40)}...` : value;
        return (
          <Tooltip content={value} showDelay={300}>
            <Text>{truncatedValue}</Text>
          </Tooltip>
        );
      },
    },
    {
      header: 'Criador',
      id: 'userCreateId',
      accessorFn: (row) => getUserDisplayName(row.userCreateId),
      enableSorting: false,
      filterFn: 'includesString',
    },
    {
      header: 'Atualizado por',
      id: 'userUpdateId',
      accessorFn: (row) => getUserDisplayName(row.userUpdateId),
      enableSorting: false,
      filterFn: 'includesString',
    },
    columnHelper.accessor('statusString', {
      header: 'Status',
      id: 'Status',
      enableSorting: false,
      aggregationFn: 'percentPaid',
      filterFn: 'equalsString',
      size: 100,
      cell: (info) => {
        if (info.cell.getValue() === 'Pago') {
          return <TickCircleIcon color="success" marginRight={16} />;
        } else if (info.cell.getValue() === 'Vencido') {
          return <BanCircleIcon color="danger" marginRight={16} />;
        } else {
          return <BanCircleIcon color="warning" marginRight={16} />;
        }
      },
    }),
    columnHelper.accessor('isReconciliated', {
      header: 'Conciliado',
      id: 'Conciliado',
      enableSorting: false,
      filterFn: 'equalsString',
      cell: (info) => {
        if (info.cell.getValue() === 'Sim') {
          return <TickCircleIcon color="info" marginRight={16} />;
        } else {
          return <BanCircleIcon color="muted" marginRight={16} />;
        }
      },
    }),
    columnHelper.accessor('interestValue', {
      header: 'Juros',
      id: 'Juros',
      aggregationFn: 'sum',
      aggregatedCell: ({ getValue }) => {
        return formatToMoneyWithSymbol(getValue());
      },
      cell: (info) => {
        return formatToMoneyWithSymbol(info.cell.getValue());
      },
    }),
    columnHelper.accessor('fineValue', {
      header: 'Multa',
      id: 'Multa',
      aggregationFn: 'sum',
      aggregatedCell: ({ getValue }) => {
        return formatToMoneyWithSymbol(getValue());
      },
      cell: (info) => {
        return formatToMoneyWithSymbol(info.cell.getValue());
      },
    }),
    columnHelper.accessor('discountValue', {
      header: 'Desconto',
      id: 'Desconto',
      aggregationFn: 'sum',
      aggregatedCell: ({ getValue }) => {
        return formatToMoneyWithSymbol(getValue());
      },
      cell: (info) => {
        return formatToMoneyWithSymbol(info.cell.getValue());
      },
    }),
    columnHelper.accessor('value', {
      enableHiding: true,
      header: 'Valor',
      id: 'Valor',
      aggregationFn: 'sum',
      aggregatedCell: ({ getValue }) => {
        return formatToMoneyWithSymbol(getValue());
      },
      cell: (info) => {
        return info.row.original.entryType === 'Entrada' ? (
          <Text color="#3366FF">{formatToMoneyWithSymbol(info.cell.getValue())}</Text>
        ) : info.row.original.entryType === 'Saída' ? (
          <Text color="#D14343">{formatToMoneyWithSymbol(Math.abs(info.cell.getValue()))}</Text>
        ) : info.row.original.entryType === 'Transferência' ? (
          <Text color="#429777">{formatToMoneyWithSymbol(Math.abs(info.cell.getValue()))}</Text>
        ) : (
          formatToMoneyWithSymbol(info.cell.getValue())
        );
      },
    }),
    columnHelper.accessor('valueFinal', {
      enableHiding: false,
      header: 'Valor Final',
      id: 'valorFinal',
      aggregationFn: 'sum',
      aggregatedCell: ({ getValue }) => {
        return formatToMoneyWithSymbol(getValue());
      },
      size: 120,
      cell: (info) => {
        return info.row.original.entryType === 'Entrada' ? (
          <Text color="#3366FF">{formatToMoneyWithSymbol(info.cell.getValue())}</Text>
        ) : info.row.original.entryType === 'Saída' ? (
          <Text color="#D14343">{formatToMoneyWithSymbol(Math.abs(info.cell.getValue()))}</Text>
        ) : info.row.original.entryType === 'Transferência' ? (
          <Text color="#429777">{formatToMoneyWithSymbol(Math.abs(info.cell.getValue()))}</Text>
        ) : info.row.original.entryType === 'Saldo Inicial' &&
          info.row.original.isInput === true ? (
          <Text color="#3366FF">{formatToMoneyWithSymbol(info.cell.getValue())}</Text>
        ) : (
          <Text color="#D14343">{formatToMoneyWithSymbol(Math.abs(info.cell.getValue()))}</Text>
        );
      },
    }),
    columnHelper.accessor('select', {
      enableSorting: false,
      enableGrouping: false,

      filterFn: 'checkedRows',
      header: 'Selecionar',
      id: 'select',
      size: 100,
      cell: ({ row }) => {
        const selectRowHandler = (e, row) => {
          row.toggleSelected(e.target.checked);

          const selectColumn = table.getColumn('select');
          const selectedFilter = selectColumn.getFilterValue();
          selectedFilter !== undefined && selectColumn.setFilterValue(selectedFilter);
        };

        return (
          <Pane>
            <Checkbox
              checked={row.getIsSelected()}
              onChange={(e) => selectRowHandler(e, row)}
              indeterminate={row.getIsSomeSelected()}
              value={row.getIsSelected() ? 'checked' : 'unchecked'}
            />
          </Pane>
        );
      },
    }),
    columnHelper.accessor('actions', {
      enableHiding: false,
      header: 'Ações',
      id: 'actions',
      enableColumnFilter: false,
      enableSorting: false,
      enableGrouping: false,
      size: 120,
      cell: (info) => (
        <EntriesActionMenu
          entry={info.row.original}
          currentRole={currentRole}
          openDialog={openDialog}
          openCopyDialog={openCopyDialog}
          openTransferDialog={openTransferDialog}
          openInitialBalanceDialog={openInitialBalanceDialog}
          openTransferCopyDialog={openTransferCopyDialog}
          openDeleteDialog={openDeleteDialog}
          statusPaymentMutation={statusPaymentMutation}
        />
      ),
    }),
  ];
};
